import React, { useEffect } from "react";
import SEO from "../components/SEO";
import AOS from "aos";
import "aos/dist/aos.css";

const PrivacyPolicy = () => {
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100
    });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#000000] pt-20 sm:pt-24">
      <SEO 
        title="Privacy Policy"
        description="Read our Privacy Policy to understand how we collect, use, and protect your personal information at White House Publishers."
        keywords="privacy policy, data protection, personal information, White House Publishers"
        url="/privacy-policy"
      />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-b from-[#1a1a1a] to-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F4D03F] mb-4 uppercase">
              Privacy Policy
            </h1>
            <p className="text-lg text-[#dfddd6]">
              Last Updated: January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-invert max-w-none">
              
              {/* Introduction */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Privacy Policy for White House Publishers</h2>
                <p className="text-[#dfddd6] text-base sm:text-lg leading-relaxed mb-4">
                  This privacy notice outlines the information collection practices on our website. By using this website, you agree to the following terms:
                </p>
              </div>

              {/* Information Collection, Use, and Sharing */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Information Collection, Use, and Sharing</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    We are the sole owners of the information collected on this website. We only access information that you voluntarily provide to us through email, forms, or direct contact. We do not sell or rent this information to anyone. The information is used solely to respond to your inquiries or requests. Occasionally, we may contact you via email to inform you about new services, updates, or special offers unless you opt out.
                  </p>
                  <p className="text-[#dfddd6] leading-relaxed">
                    We do not share your personal information with any third parties, except when necessary to fulfill your requests or as required by law.
                  </p>
                </div>
              </div>

              {/* Your Access to and Control Over Information */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Your Access to and Control Over Information</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-3">At any time, you have the right to:</p>
                  <ul className="list-disc list-inside text-[#dfddd6] space-y-2 ml-4">
                    <li>View the information we have collected about you.</li>
                    <li>Update or correct any data we maintain.</li>
                    <li>Request that we delete any information we have on file.</li>
                    <li>Express concerns regarding the use of your data.</li>
                  </ul>
                  <p className="text-[#dfddd6] mt-4">
                    To exercise these rights, please contact us using the details provided on our website.
                  </p>
                </div>
              </div>

              {/* Security */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Security</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    We take your privacy seriously and are committed to securing your personal information. All sensitive data is stored securely, and only authorized employees or contractors who need access for a specific function (e.g., processing orders or billing) can access it. Our systems are protected against unauthorized access.
                  </p>
                </div>
              </div>

              {/* Payment Information */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Payment Information</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    To process payments, we may collect personal details such as your name, email address, billing address, and phone number. We use trusted third-party payment processors (e.g., PayPal or Stripe) to handle your credit card information securely.
                  </p>
                </div>
              </div>

              {/* IP Address and Cookies */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">IP Address and Cookies</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    We automatically collect non-personal information such as your IP address when you visit our website to diagnose technical issues and improve performance. Cookies are used to enhance your user experience and to gather usage data for internal purposes, such as tracking and improving site performance.
                  </p>
                </div>
              </div>

              {/* Product and Service Information */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Product and Service Information</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    We strive to provide accurate descriptions of our services. However, we do not guarantee that all descriptions, content, or any other details on our website are always complete, up to date, or error-free. If you feel that the services you received differ from what is described, please contact us for clarification.
                  </p>
                </div>
              </div>

              {/* Confidentiality and Ownership */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Confidentiality and Ownership</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    We respect the confidentiality of your projects. All communications and content developed with you will remain confidential. You retain full ownership of all materials, and we claim no rights to the work we produce for you. As a client, you are the sole author and owner of the final product.
                  </p>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    Your contact information including your phone number, email, etc. that you share with us will never be shared with any third-party service such as an SMS campaign for purposes unrelated to providing you with the services of that campaign. We may share your Personal Data, including your SMS opt-in or consent status, with third parties that help us provide our messaging services, including but not limited to platform providers, phone companies, and any other vendors who assist us in the delivery of text messages.
                  </p>
                  <p className="text-[#dfddd6] leading-relaxed">
                    No mobile information will be shared with third parties/affiliates for marketing/promotional purposes, excluding text messaging opt-in data and consent. Message and data rates may apply. Message frequency will vary, and you can opt-out by replying "STOP". For more details on our privacy policy, please visit our website.
                  </p>
                </div>
              </div>

              {/* Originality */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Originality</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] leading-relaxed">
                    White House Publishers guarantees that all content we create, including ghostwriting services, is original, free from plagiarism, and properly cited if based on external sources.
                  </p>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Disclaimer</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    All logos, trademarks, and content displayed on this website are the property of their respective owners. White House Publishers is not affiliated, endorsed, or connected with these entities. All content provided on our website is for informational purposes only and should not be considered professional advice.
                  </p>
                  <p className="text-[#dfddd6] mb-4 leading-relaxed">
                    While we strive for accuracy, we do not guarantee the completeness or reliability of the information. Any reliance on the content is at your own risk.
                  </p>
                  <p className="text-[#dfddd6] leading-relaxed">
                    If you believe we are not adhering to this privacy policy, please contact us immediately via the contact details provided on our website.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-8" data-aos="fade-up">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-4">Contact Us</h2>
                <div className="bg-[#302f2c] p-6 rounded-lg">
                  <p className="text-[#dfddd6] mb-3">
                    If you have any questions or concerns about this Privacy Policy, please contact us:
                  </p>
                  <ul className="text-[#dfddd6] space-y-2">
                    <li><strong className="text-[#F4D03F]">Email:</strong> privacy@whitehousepublishers.com</li>
                    <li><strong className="text-[#F4D03F]">Phone:</strong> +1(833) 201 7583</li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;